//------------------------------------------------------------------------------
// BFA10102G 共架申込 基本情報 - 電柱選択
//------------------------------------------------------------------------------
import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import '@arcgis/map-components/components/arcgis-map';
import '@arcgis/map-components/components/arcgis-scale-bar';
import {
  ArcgisMap,
  ArcgisScaleBar,
} from '@arcgis/map-components-react'; // 地図ラッパー (React 19 なら不要)
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';

import appConfig from '@/config';
import MapWheelSetting, { enableWheelZoom } from '@/components/map-wheel';
import MapActionBar from '@/components/map-action-bar';
import MapActionButton from '@/components/map-action';
import MapActionLayout from '@/components/map-action-layout';
import MapCompassButton from '@/components/map-compass';
import MapSearchButton from '@/components/map-search';
import MapZoomButton from '@/components/map-zoom';
import { usePopupPortal, PopupPortal } from '@/components/map-popup';
import PoleItem from '@/utils/pole-item';
import mapUtils from '@/utils/map-utils';
import polePopupSelected from './pole-popup';
import { drawPoles, popupTitle, PopupContent } from './draw-poles';

// 定数
const ZOOM_INIT = 18;

/**
 * 電柱選択 - 地図
 */
export default function PoleSelectMap({
  poleList = [],
  selected = null,
  onSelect,
}) {

  // 選択電柱あり or 電柱一覧あり
  return (selected?.drawable() || poleList.length !== 0)

      // 地図表示
      ? <PoleSelectMapElement
          poleList={poleList}
          selected={selected}
          onSelect={onSelect}
        />

      // 地図表示なし
      : <PoleSelectNoMap />;
}

// プロパティ
PoleSelectMap.propTypes = {
  selected: PropTypes.object,
  poleList: PropTypes.array.isRequired,
  onSelect: PropTypes.func.isRequired,
};

/**
 * 電柱選択 - 地図表示
 */
function PoleSelectMapElement({
  poleList = [],
  selected = null,
  onSelect,
}) {
  // 地図参照
  const viewRef = React.useRef();
  const layersRef = React.useRef();

  // 地図に渡すプロパティ (最新の値を ref 経由で渡す)
  const propValues = React.useRef();
  React.useEffect(() => {
    propValues.current = { poleList, selected, onSelect };
  });

  // ポップアップポータル
  const popupPortal = usePopupPortal(popupTitle);

  // マウスホイール動作状態 (ホイールで拡大/縮小)
  const wheelZoom = useSelector((state) => state.setting.wheelZoom);

  //
  // 初期化処理
  //
  const handleReady = React.useCallback((event) => {
    const view = event.target?.view;
    viewRef.current = event.target?.view;

    // ズーム初期値
    view.zoom = ZOOM_INIT;

    // ポップアップ設定
    view.popup = mapUtils.popupConfig();
    view.popupEnabled = false;

    // ホイール動作設定
    enableWheelZoom(view, wheelZoom);

    // 描画レイヤー作成
    const layers = {
      draw: new GraphicsLayer({ title: '電柱一覧' }),

      // 電柱マスターレイヤー作成
      master: new FeatureLayer({
        title: '電柱マスタ',
        portalItem: { id: appConfig.gis.masterLayer },
        popupEnabled: false,
        popupTemplate: popupPortal.template,
        outFields: ['*'],
      }),
    };
    layersRef.current = layers;


    // レイヤー登録
    view.map.add(layers.master);
    view.map.add(layers.draw);
    view.whenLayerView(layers.master).then((masterView) => {

      // 電柱マスタのレイヤービュー
      layers.masterView = masterView;

      // 選択電柱ポップアップ
      const initialSelected = propValues.current?.selected;
      polePopupSelected({
        view,
        layers: layers,
        selected: initialSelected,
        template: popupPortal.template,
      });
    });

    // 電柱描画
    const initialPoles = propValues.current?.poleList;
    drawPoles(layers.draw, popupPortal.template, initialPoles);

    // 代表電柱中央表示
    const initialSelected = propValues.current?.selected;
    if (initialSelected?.drawable()) {
      const center = [
        initialSelected.longitude,
        initialSelected.latitude,
      ];
      view.goTo(center)
          .catch((err) => console.info('Map error:', err.message));
    }

    // 電柱一覧全体表示
    else if (poleList?.length) {
      const noAnimation = {};
      mapUtils.showAll(view, poleList, noAnimation);
    }
  }, []);

  //
  // 地図クリック処理
  //
  const handleViewClick = React.useCallback(async (event) => {
    const { selected, onSelect } = propValues.current;
    const view = event.target.view;
    const layers = layersRef.current;

    // 描画レイヤーの電柱を検索
    const hit = await view.hitTest(event.detail, { include: layers.draw });
    for (const result of hit.results) {
      const graphic = result?.graphic;
      const pole = graphic?.attributes;
      if (pole) {

        // 選択電柱を更新
        if (!selected?.same(pole)) {
          const selectedPole = new PoleItem(pole);
          onSelect(selectedPole);
        }

        // 電柱ポップアップ
        view.openPopup({
          location: graphic.geometry,
          featrues: [ graphic ],
        });
        return;
      }
    }

    // 電柱マスタレイヤーの電柱を検索
    const masterView = layersRef.current.masterView;
    if (masterView) {
      const query = masterView.createQuery();
      query.geometry = event.detail.mapPoint;
      query.distance = 10 * view.resolution;

      // レイヤー検索
      const result = await masterView.queryFeatures(query);
      for (const feature of result.features) {
        const pole = feature.attributes;

        // 結果判定 - 電柱
        if (pole.areaCode && pole.lineCode && pole.poleNo) {

          // 選択電柱を更新
          if (!selected?.same(pole)) {
            const selectedPole = new PoleItem(pole);
            onSelect(selectedPole);
          }

          // 電柱ポップアップ
          view.openPopup({
            location: feature.geometry,
            featrues: [ feature ],
          });
          return;
        }
      }
    }

    // ポップアップを閉じる
    view.closePopup();
  }, []);

  //
  // 電柱一覧変更
  //
  React.useEffect(() => {
    const view = viewRef.current;
    if (view) {
      console.debug('Changing pole list',
          `poles=${poleList.length}`,
          `selected=${selected?.fullText()}`);

      // 電柱描画
      const layers = layersRef.current;
      drawPoles(layers.draw, popupPortal.template, poleList);

      // 一覧の中央を表示
      mapUtils.showAll(view, poleList);
    }
  }, [poleList]);

  //
  // 選択電柱変更
  //
  React.useEffect(() => {
    const view = viewRef.current;
    if (view) {
      console.debug('Changing selected, pole',
          `poles=${poleList.length}`,
          `selected=${selected?.fullText()}`);

      // 選択電柱ポップアップ
      polePopupSelected({
        view, selected,
        layers: layersRef.current,
        template: popupPortal.template,
      });
    }
  }, [selected]);

  //
  // 選択電柱表示ボタン
  //
  const handleShowSelected = React.useCallback(() => {
    const view = viewRef.current;
    if (!view || !selected) return;

    // 選択電柱を中央に表示
    const center = [ selected.longitude, selected.latitude ];
    const target = { center, zoom: ZOOM_INIT }; // ズームも初期化
    view.goTo(target, mapUtils.GOTO_OPTIONS)
        .catch((err) => console.info('Map error:', err.message));

    // 電柱ポップアップ
    polePopupSelected({
      view, selected,
      layers: layersRef.current,
      template: popupPortal.template,
    });
  }, [selected]);

  //
  // 電柱一覧表示ボタン
  //
  const handleShowPoleList = () => {
    const view = viewRef.current;
    mapUtils.showAll(view, poleList);
  }

  //
  // マップ描画
  //
  return (
    <Box
      sx={{
        m: 0, p: 0, height: '100%',
        border: 1, borderColor: 'grey.400',
      }}
    >
      <ArcgisMap
        id="pole-select-map"
        basemap='arcgis/topographic'
        onArcgisViewReadyChange={handleReady}
        onArcgisViewClick={handleViewClick}
      >
        {/* アクションバー */}
        <MapActionBar slot="top-left">

          {/* 電柱一覧ボタン */}
          <MapActionButton
            icon="content-side-by-side"
            text="電柱一覧表示"
            title="検索した電柱一覧の範囲に地図を移動します。"
            onClick={handleShowPoleList}
          />

          {/* 選択電柱表示ボタン */}
          <MapActionButton
            icon="pop-up-blank"
            text="代表電柱表示"
            title="選択した代表電柱を地図に表示します。"
            onClick={handleShowSelected}
          />

          {/* ボタンレイアウト切替 */}
          <MapActionLayout />
        </MapActionBar>

        {/* 検索ボックス */}
        <MapSearchButton slot="top-right" />

        {/* ズームボタン */}
        <MapZoomButton slot="top-right" />

        {/* ホイール設定ボタン */}
        <MapWheelSetting slot="top-right" viewRef={viewRef} />

        {/* コンパス */}
        <MapCompassButton slot="top-right" />

        {/* スケールバー */}
        <ArcgisScaleBar
          slot="bottom-right"
          barStyle="line" unit="metric"
        />

        {/* 電柱ポップアップ */}
        <PopupPortal container={popupPortal.container}>
          <PopupContent pole={popupPortal.attribute} />
        </PopupPortal>
      </ArcgisMap>
    </Box>
  );
}

// プロパティ
PoleSelectMapElement.propTypes = {
  selected: PropTypes.object,
  poleList: PropTypes.array.isRequired,
  onSelect: PropTypes.func.isRequired,
};

/**
 * 電柱選択 - 地図なし
 */
function PoleSelectNoMap () {
  return (
    <Typography
      variant="body2"
      color="grey.500"
      sx={{
        m: 0, p: 2, height: '100%',
        border: 1, borderColor: 'grey.400',
      }}
    >
      電柱を検索すると地図を表示します
    </Typography>
  );
}
